<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useDebounceFn } from '@vueuse/core';
import { buildOrderedStatusOptions } from 'dashboard/routes/dashboard/planning/events/statusOptions';

const props = defineProps({
  modelValue: { type: [String, Number], default: null },
  perPage: { type: Number, default: 50 },
});

const emit = defineEmits(['update:modelValue']);

const store = useStore();

const events = computed(() => store.getters['planningEvents/getRecords'] || []);
const isFetching = computed(
  () => store.getters['planningEvents/getUIFlags']?.isFetching
);

const searchQuery = ref('');
const dropdownOpen = ref(false);
const statusFilter = ref('');
const startDate = ref('');
const endDate = ref('');
const statusOptions = ref([]);
const showFilters = ref(false);

const selectedEvent = computed(() =>
  events.value.find(e => e.id === props.modelValue)
);

const filteredEvents = computed(() => {
  if (!searchQuery.value) return events.value;
  const q = searchQuery.value.toLowerCase();
  return events.value.filter(e => e.name?.toLowerCase().includes(q));
});

const formatDate = dateStr => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const buildParams = () => {
  const params = { per_page: props.perPage };
  if (statusFilter.value) {
    params.status_keys = [statusFilter.value];
  }
  if (startDate.value && endDate.value) {
    params.start_date = startDate.value;
    params.end_date = endDate.value;
  }
  return params;
};

const fetchEvents = async () => {
  await store.dispatch('planningEvents/get', buildParams());
};

const debouncedFetch = useDebounceFn(fetchEvents, 300);

const loadStatusOptions = async () => {
  const response = await store.dispatch('planningEvents/getStatusOptions');
  statusOptions.value = buildOrderedStatusOptions(response);
};

const selectEvent = event => {
  emit('update:modelValue', event.id);
  searchQuery.value = '';
  dropdownOpen.value = false;
};

const clearSelection = () => {
  emit('update:modelValue', null);
};

const openDropdown = () => {
  dropdownOpen.value = true;
};

const closeDropdown = () => {
  setTimeout(() => {
    dropdownOpen.value = false;
    searchQuery.value = '';
  }, 150);
};

const toggleFilters = () => {
  showFilters.value = !showFilters.value;
};

const clearFilters = () => {
  statusFilter.value = '';
  startDate.value = '';
  endDate.value = '';
  fetchEvents();
};

const hasActiveFilters = computed(
  () => statusFilter.value || (startDate.value && endDate.value)
);

watch(statusFilter, () => debouncedFetch());
watch([startDate, endDate], () => {
  if (
    (startDate.value && endDate.value) ||
    (!startDate.value && !endDate.value)
  ) {
    debouncedFetch();
  }
});

onMounted(async () => {
  await Promise.all([fetchEvents(), loadStatusOptions()]);
});
</script>

<template>
  <div class="space-y-1.5">
    <!-- Selected event chip -->
    <div
      v-if="selectedEvent && !dropdownOpen"
      class="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md bg-n-brand-3 text-n-brand-11"
    >
      <div class="flex flex-col min-w-0">
        <span class="text-xs font-medium truncate">
          {{ selectedEvent.name }}
        </span>
        <span
          v-if="selectedEvent.start_date"
          class="text-[10px] opacity-70 mt-0.5"
        >
          {{ formatDate(selectedEvent.start_date) }}
        </span>
      </div>
      <button
        class="shrink-0 p-0.5 rounded hover:bg-n-brand-4 transition-colors"
        @click="clearSelection"
      >
        <span class="i-lucide-x size-3" />
      </button>
    </div>

    <!-- Search + dropdown -->
    <div v-else class="relative">
      <div class="flex items-center gap-1">
        <div class="relative flex-1">
          <span
            class="i-lucide-search size-3 absolute left-2 top-1/2 -translate-y-1/2 text-n-slate-9"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search events..."
            class="reset-base no-margin w-full pl-7 pr-2 py-1.5 text-xs border border-n-strong rounded-md bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-1 focus:ring-n-brand-7"
            @focus="openDropdown"
            @blur="closeDropdown"
          />
        </div>
        <button
          class="shrink-0 p-1 rounded-md border transition-colors"
          :class="
            hasActiveFilters
              ? 'border-n-brand-7 bg-n-brand-3 text-n-brand-11'
              : 'border-n-strong text-n-slate-9 hover:bg-n-slate-3'
          "
          @click="toggleFilters"
        >
          <span class="i-lucide-sliders-horizontal size-3.5" />
        </button>
      </div>

      <!-- Filters panel -->
      <div
        v-if="showFilters"
        class="mt-1.5 p-2 rounded-md border border-n-strong bg-n-slate-2 space-y-2"
      >
        <div>
          <label class="block text-[10px] font-medium text-n-slate-10 mb-0.5">
            Status
          </label>
          <select
            v-model="statusFilter"
            class="reset-base no-margin w-full px-2 py-1 text-xs border border-n-strong rounded bg-n-background text-n-slate-12"
          >
            <option value="">All statuses</option>
            <option
              v-for="opt in statusOptions"
              :key="opt.key"
              :value="opt.key"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="grid grid-cols-2 gap-1.5">
          <div>
            <label class="block text-[10px] font-medium text-n-slate-10 mb-0.5">
              From
            </label>
            <input
              v-model="startDate"
              type="date"
              class="reset-base no-margin w-full px-2 py-1 text-xs border border-n-strong rounded bg-n-background text-n-slate-12"
            />
          </div>
          <div>
            <label class="block text-[10px] font-medium text-n-slate-10 mb-0.5">
              To
            </label>
            <input
              v-model="endDate"
              type="date"
              class="reset-base no-margin w-full px-2 py-1 text-xs border border-n-strong rounded bg-n-background text-n-slate-12"
            />
          </div>
        </div>
        <button
          v-if="hasActiveFilters"
          class="text-[10px] text-n-brand-11 hover:underline"
          @click="clearFilters"
        >
          Clear filters
        </button>
      </div>

      <!-- Dropdown results -->
      <div
        v-if="dropdownOpen"
        class="mt-1 max-h-64 overflow-y-auto rounded-md border border-n-strong bg-n-background"
      >
        <div v-if="isFetching" class="flex items-center justify-center py-3">
          <span
            class="i-lucide-loader-2 size-3.5 animate-spin text-n-slate-9"
          />
        </div>
        <template v-else>
          <button
            v-for="event in filteredEvents"
            :key="event.id"
            class="flex flex-col w-full px-2 py-1.5 text-left transition-colors text-n-slate-11 hover:bg-n-slate-3"
            @mousedown.prevent="selectEvent(event)"
          >
            <span class="text-xs font-medium truncate w-full">
              {{ event.name }}
            </span>
            <span
              v-if="event.start_date"
              class="text-[10px] text-n-slate-9 mt-0.5"
            >
              {{ formatDate(event.start_date) }}
            </span>
          </button>
          <p
            v-if="!filteredEvents.length"
            class="text-[10px] text-n-slate-9 px-2 py-2 italic text-center"
          >
            No events found
          </p>
        </template>
      </div>
    </div>
  </div>
</template>
